import React, { createContext, ReactNode, useEffect, useReducer, useContext, useState } from 'react'

import {
  ChatHistoryLoadingState,
  Conversation,
  CosmosDBHealth,
  CosmosDBStatus,
  environmentVariablesList,
  EnvironmentVariablesList,
  Feedback,
  FrontendSettings,
  frontendSettings,
  historyEnsure,
  historyList,
  indexesList,
  IndexesList,
  templatesList,
  TemplatesList,
  countryList,
  CountryList,
  countriesPrompt,
  yearPrompt,
  filterYears
} from '../api'

import { appStateReducer } from './AppReducer'

export interface AppState {
  isChatHistoryOpen: boolean
  chatHistoryLoadingState: ChatHistoryLoadingState
  isCosmosDBAvailable: CosmosDBHealth
  chatHistory: Conversation[] | null
  filteredChatHistory: Conversation[] | null
  currentChat: Conversation | null
  frontendSettings: FrontendSettings | null
  environmentVariablesList: EnvironmentVariablesList | null
  indexesList: IndexesList | null
  templatesList: TemplatesList | null
  countryList: CountryList | null
  feedbackState: { [answerId: string]: Feedback.Neutral | Feedback.Positive | Feedback.Negative }
  isLoading: boolean
  answerExecResult: { [answerId: string]: [] }
  countryprompt: string | null
  yearprompt: string | null
  yearlist: string | null
  layoutButtonAction?: () => void
}

export type Action =
  | { type: 'TOGGLE_CHAT_HISTORY' }
  | { type: 'SET_COSMOSDB_STATUS'; payload: CosmosDBHealth }
  | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE'; payload: ChatHistoryLoadingState }
  | { type: 'UPDATE_CURRENT_CHAT'; payload: Conversation | null }
  | { type: 'UPDATE_FILTERED_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'UPDATE_CHAT_HISTORY'; payload: Conversation }
  | { type: 'UPDATE_CHAT_TITLE'; payload: Conversation }
  | { type: 'DELETE_CHAT_ENTRY'; payload: string }
  | { type: 'DELETE_CHAT_HISTORY' }
  | { type: 'DELETE_CURRENT_CHAT_MESSAGES'; payload: string }
  | { type: 'FETCH_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'FETCH_FRONTEND_SETTINGS'; payload: FrontendSettings | null }
  | { type: 'FETCH_ENVIRONMENT_VARIABLES'; payload: EnvironmentVariablesList | null } // API Call
  | { type: 'FETCH_INDEXES_LIST'; payload: IndexesList | null } // API Call
  | { type: 'FETCH_TEMPLATES_LIST'; payload: TemplatesList | null } // API Call
  | { type: 'FETCH_COUNTRY_LIST'; payload: CountryList | null } // API Call
  | { type: 'FETCH_YEAR_PROMPT'; payload: string | null } // API Call
  | { type: 'FETCH_COUNTRY_PROMPT'; payload: string | null } // API Call
  | { type: 'FETCH_YEAR_LIST'; payload: string | null } // API Call
  | {
      type: 'SET_FEEDBACK_STATE'
      payload: { answerId: string; feedback: Feedback.Positive | Feedback.Negative | Feedback.Neutral }
    }
  | { type: 'GET_FEEDBACK_STATE'; payload: string }
  | { type: 'SET_ANSWER_EXEC_RESULT'; payload: { answerId: string; exec_result: [] } }
  | { type: 'SET_LAYOUT_BUTTON_ACTION'; payload?: () => void }

const initialState: AppState = {
  isChatHistoryOpen: false,
  chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
  chatHistory: null,
  filteredChatHistory: null,
  currentChat: null,
  isCosmosDBAvailable: {
    cosmosDB: false,
    status: CosmosDBStatus.NotConfigured
  },
  frontendSettings: null,
  environmentVariablesList: null,
  indexesList: null,
  templatesList: null,
  countryList: null,
  countryprompt: null,
  yearprompt: null,
  yearlist: null,
  feedbackState: {},
  isLoading: true,
  answerExecResult: {}
}

export const AppStateContext = createContext<
  | {
      state: AppState
      dispatch: React.Dispatch<Action>
    }
  | undefined
>(undefined)

type AppStateProviderProps = {
  children: ReactNode
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState)

  useEffect(() => {
    // Check for cosmosdb config and fetch initial data here
    const fetchChatHistory = async (offset = 0): Promise<Conversation[] | null> => {
      const result = await historyList(offset)
        .then(response => {
          if (response) {
            dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response })
          } else {
            dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
          }
          return response
        })
        .catch(_err => {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
          console.error('There was an issue fetching your data.')
          return null
        })
      return result
    }

    const getHistoryEnsure = async () => {
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading })
      historyEnsure()
        .then(response => {
          if (response?.cosmosDB) {
            fetchChatHistory()
              .then(res => {
                if (res) {
                  dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success })
                  dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response })
                } else {
                  dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
                  dispatch({
                    type: 'SET_COSMOSDB_STATUS',
                    payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking }
                  })
                }
              })
              .catch(_err => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
                dispatch({
                  type: 'SET_COSMOSDB_STATUS',
                  payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking }
                })
              })
          } else {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
            dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response })
          }
        })
        .catch(_err => {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } })
        })
    }
    getHistoryEnsure()
  }, [])

  useEffect(() => {
    const getFrontendSettings = async () => {
      frontendSettings()
        .then(response => {
          dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: response as FrontendSettings })
        })
        .catch(_err => {
          console.error('There was an issue fetching your data.')
        })
    }
    getFrontendSettings()
  }, [])

  useEffect(() => {
    const getIndexesList = async () => {
      indexesList()
        .then(response => {
          dispatch({ type: 'FETCH_INDEXES_LIST', payload: response as IndexesList })
        })
        .catch(err => {
          console.error('There was an issue fetching Azure AI Search indexes list.')
        })
    }
    getIndexesList()
  }, [])

  useEffect(() => {
    const getTemplatesList = async () => {
      templatesList()
        .then(response => {
          dispatch({ type: 'FETCH_TEMPLATES_LIST', payload: response as TemplatesList })
        })
        .catch(err => {
          console.error('There was an issue fetching templates list.')
        })
    }
    getTemplatesList()
  }, [])

  useEffect(() => {
  const getCountryList = async () => {
    countryList()
        .then(response => {
          console.log(response)
      dispatch({ type: 'FETCH_COUNTRY_LIST', payload: response as CountryList })
        })
        .catch(err => {
          console.error('There was an issue fetching templates list.')
        })
  }
  getCountryList()
}, [])

  useEffect(() => {
  const getCountryPrompt = async () => {
    countriesPrompt()
        .then(response => {
          console.log(response)
      dispatch({ type: 'FETCH_COUNTRY_PROMPT', payload: response })
        })
        .catch(err => {
          console.error('There was an issue fetching country prompt.')
        })
  }
  getCountryPrompt()
}, [])

    useEffect(() => {
  const getYearPrompt = async () => {
    yearPrompt()
        .then(response => {
          console.log(response)
      dispatch({ type: 'FETCH_YEAR_PROMPT', payload: response })
        })
        .catch(err => {
          console.error('There was an issue fetching year prompt.')
        })
  }
  getYearPrompt()
}, [])

    useEffect(() => {
  const getYearList = async () => {
    filterYears()
        .then(response => {
          console.log(response)
      dispatch({ type: 'FETCH_YEAR_LIST', payload: response })
        })
        .catch(err => {
          console.error('There was an issue fetching year prompt.')
        })
  }
  getYearList()
}, [])

  useEffect(() => {
    const getEnvironmentVariablesList = async () => {
      environmentVariablesList()
        .then(response => {
          dispatch({ type: 'FETCH_ENVIRONMENT_VARIABLES', payload: response as EnvironmentVariablesList })
        })
        .catch(err => {
          console.error('There was an issue fetching your environment variables from the server.')
        })
    }
    getEnvironmentVariablesList()
  }, [])

  return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
}
